/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
// RGBARRY-CUSTOMIZATION: Hero 1 PD component — full-bleed image hero with eyebrow, title, sub-copy, dual CTAs, legal text
import { Component } from '@/lib/decorators/component';
import { AttributeDefinition } from '@/lib/decorators/attribute-definition';
import { RegionDefinition } from '@/lib/decorators';
import { Link } from '@/components/link';
import { StretchedLink } from '@/components/stretched-link';
import { DynamicImage } from '@/components/dynamic-image';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import type { Image } from '@/types';

type HeroFont = 'Current Font Style' | 'Inter';

const HEX_COLOR_REGEX = /^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;

const parseOptionalHex = (value: string | undefined): string | undefined => {
    const trimmed = value?.trim();
    return trimmed && HEX_COLOR_REGEX.test(trimmed) ? trimmed : undefined;
};

const normalizeFont = (value: string | undefined): HeroFont => (value === 'Inter' ? 'Inter' : 'Current Font Style');

/* v8 ignore start */
@Component('heroOne', {
    name: 'Hero 1',
    description: 'Full-bleed image hero with eyebrow, title, sub-copy, up to two CTAs, and legal text.',
    group: 'Content',
})
@RegionDefinition([])
// oxlint-disable-next-line react/only-export-components
export class HeroOneMetadata {
    @AttributeDefinition({ id: 'eyebrow', name: 'Eyebrow / Tagline', type: 'string' })
    eyebrow?: string;

    @AttributeDefinition({ name: 'Title', type: 'string' })
    title?: string;

    @AttributeDefinition({ id: 'subCopy', name: 'Sub Copy', type: 'text' })
    subCopy?: string;

    @AttributeDefinition({
        id: 'fontStyle',
        name: 'Headline and Sub Copy Font',
        description: 'Keep the current Hero 1 typography or use Inter for both the headline and sub copy.',
        type: 'enum',
        // Keep enum values inline: the cartridge generator statically parses this decorator and
        // serializes spread expressions as text instead of evaluating them.
        values: ['Current Font Style', 'Inter'],
        defaultValue: 'Current Font Style',
    })
    fontStyle?: string;

    @AttributeDefinition({
        id: 'fontColor',
        name: 'Font Color',
        description: 'Hex color for the headline and sub copy, e.g. #FFFFFF or #fff. Leave empty for the default.',
        type: 'string',
    })
    fontColor?: string;

    @AttributeDefinition({ id: 'ctaOneText', name: 'Primary CTA Text', type: 'string' })
    ctaOneText?: string;

    @AttributeDefinition({ id: 'ctaOneLink', name: 'Primary CTA Link', type: 'url' })
    ctaOneLink?: string;

    @AttributeDefinition({ id: 'ctaTwoText', name: 'Secondary CTA Text', type: 'string' })
    ctaTwoText?: string;

    @AttributeDefinition({ id: 'ctaTwoLink', name: 'Secondary CTA Link', type: 'url' })
    ctaTwoLink?: string;

    @AttributeDefinition({ id: 'legalText', name: 'Legal Text', type: 'string' })
    legalText?: string;

    @AttributeDefinition({ name: 'Image', type: 'image' })
    image?: Image;

    @AttributeDefinition({
        id: 'mobileImage',
        name: 'Mobile Image',
        description: 'Optional image used below 768px. Falls back to Image when not provided.',
        type: 'image',
    })
    mobileImage?: Image;

    @AttributeDefinition({ id: 'imageAlt', name: 'Image Alt Text', type: 'string' })
    imageAlt?: string;

    @AttributeDefinition({
        id: 'ctaColor',
        name: 'CTA Color',
        description: 'Hex background color for both CTA buttons, e.g. #FFFFFF or #fff. Leave empty for the default.',
        type: 'string',
    })
    ctaColor?: string;

    @AttributeDefinition({
        id: 'ctaFontColor',
        name: 'CTA Font Color',
        description: 'Hex text color for both CTA buttons, e.g. #1A1A1A or #000. Leave empty for the default.',
        type: 'string',
    })
    ctaFontColor?: string;
}
/* v8 ignore stop */

export default function HeroOne({
    eyebrow,
    title,
    subCopy,
    fontStyle,
    fontColor,
    ctaOneText,
    ctaOneLink,
    ctaTwoText,
    ctaTwoLink,
    legalText,
    image,
    mobileImage,
    imageAlt,
    ctaColor,
    ctaFontColor,
}: {
    eyebrow?: string;
    title?: string;
    subCopy?: string;
    fontStyle?: string;
    fontColor?: string;
    ctaOneText?: string;
    ctaOneLink?: string;
    ctaTwoText?: string;
    ctaTwoLink?: string;
    legalText?: string;
    image?: Image;
    mobileImage?: Image;
    imageAlt?: string;
    ctaColor?: string;
    ctaFontColor?: string;
}) {
    const selectedFont = normalizeFont(fontStyle);
    const authoredFontColor = parseOptionalHex(fontColor);
    const authoredCtaColor = parseOptionalHex(ctaColor);
    const authoredCtaFontColor = parseOptionalHex(ctaFontColor);
    const heroImage = image ?? mobileImage;
    // RGBARRY-CUSTOMIZATION: keep a link-only Hero 1 accessible when no visible primary CTA supplies
    // the destination's accessible name; use authored copy first and the URL as a final non-empty fallback.
    const heroLinkLabel = imageAlt?.trim() || title?.trim() || eyebrow?.trim() || ctaOneLink;
    // END RGBARRY-CUSTOMIZATION
    const titleStyle = {
        fontFamily: selectedFont === 'Inter' ? 'var(--font-sans)' : 'var(--font-display)',
        ...(authoredFontColor ? { color: authoredFontColor } : {}),
    };
    const subCopyStyle = {
        ...(selectedFont === 'Inter' ? { fontFamily: 'var(--font-sans)' } : {}),
        ...(authoredFontColor ? { color: authoredFontColor } : {}),
    };
    const ctaStyle =
        authoredCtaColor || authoredCtaFontColor
            ? { backgroundColor: authoredCtaColor, color: authoredCtaFontColor }
            : undefined;

    return (
        <div className="relative w-full lg:min-h-[87.5vh] bg-background overflow-hidden">
            {heroImage?.url && (
                <DynamicImage
                    src={heroImage}
                    mobileImage={mobileImage}
                    alt={imageAlt ?? ''}
                    widths={['100vw']}
                    // Full-bleed hero is the desktop LCP. Preload it at high priority (React 19 emits a
                    // <link rel="preload" as="image" fetchpriority="high"> during SSR) so it loads from the
                    // initial HTML instead of queuing behind the route's JS modulepreload graph. This and the
                    // promo-countdown banner image are both intentionally high priority per product direction.
                    priority="high"
                    // RGBARRY-CUSTOMIZATION: below lg the image renders at its intrinsic ratio (never cropped);
                    // from lg the hero is at least viewport-height and a taller asset grows it rather than cropping.
                    className="w-full [&_picture]:block [&_picture]:w-full"
                    imageProps={{ className: 'block w-full h-auto lg:min-h-[87.5vh] object-cover' }}
                />
            )}

            {/* RGBARRY-CUSTOMIZATION: a primary link always makes the whole hero image clickable, even
                without CTA text. When a visible CTA exists, keep the duplicate overlay out of the
                accessibility tree; otherwise expose one named, keyboard-focusable full-bleed link. */}
            {ctaOneLink &&
                (ctaOneText ? (
                    <StretchedLink to={ctaOneLink} className="z-[6]" />
                ) : (
                    <Link to={ctaOneLink} aria-label={heroLinkLabel} className="absolute inset-0 z-[6]" />
                ))}

            <div className="absolute inset-0 z-10 flex items-center pointer-events-none">
                <div className="px-6 sm:px-10 lg:px-14 max-w-2xl">
                    {eyebrow && (
                        <p className="df-hero-label text-xs tracking-[0.22em] uppercase text-primary-foreground/70 mb-4">
                            {eyebrow}
                        </p>
                    )}
                    {title && (
                        <h2
                            style={titleStyle}
                            className="df-hero-h1 text-4xl sm:text-5xl lg:text-6xl font-light leading-[1.05] tracking-tight text-primary-foreground mb-6">
                            {title}
                        </h2>
                    )}
                    {subCopy && (
                        <p
                            style={subCopyStyle}
                            className="df-hero-body text-sm lg:text-base text-primary-foreground/80 leading-relaxed mb-8 max-w-md">
                            {subCopy}
                        </p>
                    )}
                    {(ctaOneText && ctaOneLink) || (ctaTwoText && ctaTwoLink) ? (
                        <div className="flex flex-wrap gap-3 mb-4 pointer-events-auto">
                            {ctaOneText && ctaOneLink && (
                                <Button
                                    asChild
                                    style={ctaStyle}
                                    className="df-hero-cta df-btn-shimmer rounded-none h-12 min-w-[124px] px-6 py-3 gap-2 bg-background text-foreground hover:bg-background/90">
                                    <Link to={ctaOneLink}>{ctaOneText}</Link>
                                </Button>
                            )}
                            {ctaTwoText && ctaTwoLink && (
                                <Button
                                    asChild
                                    variant="outline"
                                    style={ctaStyle}
                                    className="df-hero-cta rounded-none h-12 min-w-[124px] px-6 py-3 gap-2 border-primary-foreground text-primary-foreground hover:bg-primary-foreground hover:text-foreground">
                                    <Link to={ctaTwoLink}>{ctaTwoText}</Link>
                                </Button>
                            )}
                        </div>
                    ) : null}
                    {legalText && (
                        <p className={cn('df-hero-cta text-[11px] text-primary-foreground/50 leading-relaxed')}>
                            {legalText}
                        </p>
                    )}
                </div>
            </div>
        </div>
    );
}
